import { useEffect } from "react";
import { Controller, ControllerRenderProps } from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { formatNumber } from "@/utils/generic";
import { RangeSlider } from "../shadcn/slider";
import { NumberInput } from "./NumberInputField";

interface INumberRangeSlider {
  value: [number, number];
  max?: number;
  min?: number;
  onChange: (v: [number, number]) => any;
  tooltip?: string;
  label: string;
  isDisabled?: boolean;
  step?: number;
  hideNumber?: boolean;
  name?: string;
  helperText?: string;
}

export const NumberRangeSlider = ({
  value,
  min = 0,
  max = 100,
  tooltip,
  label,
  onChange,
  isDisabled,
  step,
  hideNumber,
  name,
  helperText,
}: INumberRangeSlider) => {
  const [low, high] = value;

  const handleLowChange = (v: number) => {
    onChange([Math.max(min, Math.min(v, high)), high]);
  };

  const handleHighChange = (v: number) => {
    onChange([low, Math.min(max, Math.max(v, low))]);
  };

  const handleSliderChange = (values: number[]) => {
    onChange([values[0], values[1]]);
  };

  return (
    <div className="w-full">
      {label && <NoxusFormLabel label={label} tooltip={tooltip} name={name} />}
      <div className="flex gap-6 items-center">
        <div className="w-24">
          <NumberInput
            value={low}
            min={min}
            max={high}
            onChange={handleLowChange}
            isDisabled={isDisabled}
            placeholder="0"
            step={step}
          />
        </div>
        <div className="flex-1">
          <RangeSlider
            value={[low, high]}
            onValueChange={handleSliderChange}
            min={min}
            max={max}
            step={step}
            disabled={isDisabled}
            className="w-full"
            showValue={
              !hideNumber
                ? (v: number) => formatNumber(v) ?? String(v)
                : undefined
            }
          />
        </div>
        <div className="w-24">
          <NumberInput
            value={high}
            min={low}
            max={max}
            onChange={handleHighChange}
            isDisabled={isDisabled}
            placeholder="0"
            step={step}
          />
        </div>
      </div>
      {helperText && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}
    </div>
  );
};

const isRangeTuple = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((v) => typeof v === "number");

const normalizeRange = (
  value: unknown,
  min: number,
  max: number
): [number, number] => (isRangeTuple(value) ? value : [min, max]);

interface IControlledNumberRangeSlider extends Omit<
  INumberRangeSlider,
  "value" | "onChange"
> {
  field: ControllerRenderProps;
  min: number;
  max: number;
}

const ControlledNumberRangeSlider = ({
  field,
  min,
  max,
  ...rest
}: IControlledNumberRangeSlider) => {
  const value = normalizeRange(field.value, min, max);

  // Sync malformed stored values into form state, not just the display.
  useEffect(() => {
    if (!isRangeTuple(field.value)) {
      field.onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  return (
    <NumberRangeSlider
      {...rest}
      min={min}
      max={max}
      value={value}
      onChange={field.onChange}
    />
  );
};

interface INumberRangeSliderField {
  name: string;
  defaultValue?: [number, number];
  max?: number;
  tooltip?: string;
  label: string;
  min?: number;
  control?: any;
  isDisabled?: boolean;
  step?: number;
  hideNumber?: boolean;
  helperText?: string;
}

export const NumberRangeSliderField = ({
  name,
  label,
  defaultValue,
  max = 100,
  min = 0,
  tooltip,
  control,
  isDisabled,
  step,
  hideNumber,
  helperText,
}: INumberRangeSliderField) => {
  return (
    <Controller
      defaultValue={normalizeRange(defaultValue, min, max)}
      control={control}
      disabled={isDisabled}
      render={({ field }) => (
        <ControlledNumberRangeSlider
          field={field}
          label={label}
          max={max}
          min={min}
          tooltip={tooltip}
          isDisabled={isDisabled}
          step={step}
          hideNumber={hideNumber}
          name={name}
          helperText={helperText}
        />
      )}
      name={name}
    />
  );
};
